import { useEffect, useRef } from 'react';
import { Canvas, FabricObject } from 'fabric';
import { Composition, FabricClip } from '../timeline/types';
import { getOrAssignLayerId } from '../model/layer-id';
import { useMedia } from '../media';
import { playheadStore } from './playhead-store';

const DRIFT_THRESHOLD_S = 0.5;
const PRECISE_THRESHOLD_S = 0.05;
const SCRUB_DELTA_MS = 200;

type UseVideoSyncOptions = {
  canvas: Canvas | null;
  composition: Composition;
  isPlaying: boolean;
};

/**
 * Drives `HTMLVideoElement`s registered in the MediaProvider from the timeline.
 * Resyncs `currentTime` only on boundary cross, scrub, or large drift —
 * otherwise the video plays on its own clock.
 */
export const useVideoSync = ({ canvas, composition, isPlaying }: UseVideoSyncOptions): void => {
  const { getVideo } = useMedia();
  const previousAliveRef = useRef<Map<string, boolean>>(new Map());
  const previousPlayheadRef = useRef<number>(playheadStore.get());

  useEffect(() => {
    if (!canvas) return;

    const clipObjectIdToVideo = new Map<string, HTMLVideoElement>();
    canvas.getObjects().forEach(obj => {
      const objAny = obj as FabricObject & { id?: string };
      const videoId = objAny.id;
      if (!videoId) return;
      const el = getVideo(videoId);
      if (!el) return;
      clipObjectIdToVideo.set(getOrAssignLayerId(obj), el);
    });

    if (clipObjectIdToVideo.size === 0) return;

    const sync = () => {
      const t = playheadStore.get();
      const playheadDelta = Math.abs(t - previousPlayheadRef.current);
      const isScrub = playheadDelta > SCRUB_DELTA_MS;
      previousPlayheadRef.current = t;

      Object.values(composition.clipsById).forEach(clip => {
        if (clip.kind !== 'fabric') return;
        const fabricClip = clip as FabricClip;
        const video = clipObjectIdToVideo.get(fabricClip.objectId);
        if (!video) return;

        const localTimeS = (t - fabricClip.start) / 1000;
        const clipEndS = (fabricClip.start + fabricClip.duration) / 1000;
        const alive = t >= fabricClip.start && t < fabricClip.start + fabricClip.duration;

        const wasAlive = previousAliveRef.current.get(clip.id) ?? false;
        const boundaryCross = alive && !wasAlive;
        previousAliveRef.current.set(clip.id, alive);

        if (!alive) {
          if (!video.paused) video.pause();
          const parked = t < fabricClip.start ? 0 : Math.min(clipEndS, video.duration || 0);
          if (Math.abs(video.currentTime - parked) > DRIFT_THRESHOLD_S) {
            video.currentTime = parked;
          }
          return;
        }

        const clamped = Math.min(Math.max(0, localTimeS), video.duration || localTimeS);

        if (isPlaying) {
          if (video.paused) video.play().catch(() => undefined);
          const drift = Math.abs(video.currentTime - clamped);
          const tolerance = boundaryCross || isScrub ? PRECISE_THRESHOLD_S : DRIFT_THRESHOLD_S;
          if (drift > tolerance) {
            video.currentTime = clamped;
          }
        } else {
          if (!video.paused) video.pause();
          if (Math.abs(video.currentTime - clamped) > PRECISE_THRESHOLD_S) {
            video.currentTime = clamped;
          }
        }
      });
    };

    sync();
    return playheadStore.subscribe(sync);
  }, [canvas, composition, isPlaying, getVideo]);
};
